import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, LogIn } from 'lucide-react';

const STORAGE_KEY = 'qmg_welcome_seen_v1';

const WelcomeIntroModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Don't show on auth/admin/legal flows
    const path = window.location.pathname;
    const skip = (
      path.startsWith('/login') ||
      path.startsWith('/register') ||
      path.startsWith('/admin') ||
      path.startsWith('/coach') ||
      path.startsWith('/locker') ||
      path.startsWith('/dashboard') ||
      path.startsWith('/reset-password') ||
      path.startsWith('/forgot-password') ||
      path.startsWith('/terms') ||
      path.startsWith('/privacy') ||
      path.startsWith('/a3-assessment')
    );
    if (skip) return;

    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Slight delay so the page renders behind it first
        const t = setTimeout(() => setOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  const dismiss = (markSeen = true) => {
    if (markSeen) {
      try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
    }
    setClosing(true);
    setTimeout(() => setOpen(false), 240);
  };

  const handleExplore = () => dismiss(true);

  const handleLogin = () => {
    dismiss(true);
    navigate('/login');
  };

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-sm transition-opacity ${closing ? 'opacity-0' : 'opacity-100'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      data-testid="welcome-intro-modal"
    >
      <div
        className={`relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-white shadow-2xl transition-all ${closing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
      >
        {/* Close */}
        <button
          type="button"
          onClick={() => dismiss(true)}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#1A1A1A] flex items-center justify-center shadow-md transition-colors"
          aria-label="Close"
          data-testid="welcome-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Full image banner on top */}
        <div className="bg-[#1A2E1D] flex items-center justify-center" data-testid="welcome-image-wrap">
          <img
            src="/welcome-confidence.png"
            alt="Quiet Mind Golf — Awareness · Acceptance · Action"
            className="block w-full h-auto max-h-[60vh] object-contain"
            loading="eager"
          />
        </div>

        {/* Text section below */}
        <div className="p-8 md:p-12 bg-[#FAFAF9]">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-3">
            Welcome to Quiet Mind Golf
          </p>
          <h1
            id="welcome-title"
            className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.1] mb-5"
          >
            Human first. Golf swing second.
          </h1>

          <div className="space-y-4 text-[#57534E] leading-relaxed text-sm md:text-base max-w-3xl">
            <p>
              Too often, golfers let the swing operate the human. High-level performance begins when the relationship is reversed — <strong className="text-[#1A1A1A]">the human is the operating system of the golf swing</strong>.
            </p>
            <p>
              The <span className="font-semibold text-[#1A2E1D]">QMG AAA Blueprint</span> — <span className="text-[#2C5530] font-semibold">Awareness</span>, <span className="text-[#BC562B] font-semibold">Acceptance</span>, <span className="text-[#1A2E1D] font-semibold">Action</span> — places the human first, so the swing can run efficiently even through adversity.
            </p>
            <p className="border-l-2 border-[#BC562B] pl-4 italic text-[#1A1A1A]">
              Confidence is the willingness to act in service of your performance values — regardless of conditions, emotions, or swing feelings.
            </p>
            <p>
              When your commitment to your values is greater than your addiction to outcome, you control the outcome — not the golf swing.
            </p>
          </div>

          {/* CTAs */}
          <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
            <button
              type="button"
              onClick={handleExplore}
              className="inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-5 py-3 text-sm font-semibold hover:bg-[#1E3A21] transition-colors"
              data-testid="welcome-explore-btn"
            >
              Explore the site <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleLogin}
              className="inline-flex items-center justify-center gap-2 border border-[#1A2E1D] text-[#1A1A1A] px-5 py-3 text-sm font-semibold hover:bg-[#F2F0E9] transition-colors"
              data-testid="welcome-login-btn"
            >
              <LogIn className="w-4 h-4" /> I have an account
            </button>
          </div>

          <p className="mt-4 text-[10px] text-[#A8A29E] uppercase tracking-wider">
            You won't see this again on this device.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeIntroModal;
